import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { closeDialogAction } from 'store/global/actions';

import { LoanFeesTestIds } from '../../../../LoanFees.testIds';
import {
  FEE_RECOMMENDATION_REVIEW_NOTES_MAX_LENGTH,
  REJECT_FEE_RECOMMENDATION_FORM_ID,
} from '../../FeeRecommendationsPanel.constants';
import { buttonContentStyles } from '../../FeeRecommendationsPanel.styles';

import { descriptionStyles, formStyles } from './RejectFeeRecommendationModal.styles';

const RejectFeeRecommendationModal = ({ description, onReject, dialogId }) => {
  const dispatch = useDispatch();

  const [reviewNotes, setReviewNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const onChangeReviewNotes = useCallback(event => {
    setReviewNotes(event.target.value);
  }, []);

  const onCancel = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (isSaving) {
        return;
      }

      setIsSaving(true);

      onReject(reviewNotes.trim() || null)
        .then(() => dispatch(closeDialogAction(dialogId)))
        .catch(Error)
        .finally(() => setIsSaving(false));
    },
    [dispatch, dialogId, isSaving, onReject, reviewNotes],
  );

  return (
    <Box
      noValidate
      component="form"
      id={REJECT_FEE_RECOMMENDATION_FORM_ID}
      sx={formStyles}
      onSubmit={onSubmit}
    >
      <Typography color="text.secondary" sx={descriptionStyles} variant="regular14">
        {`Rejecting ${description}.`}
      </Typography>

      <TextFieldCDS
        autoFocus
        fullWidth
        multiline
        dataTestId={LoanFeesTestIds.FEE_RECOMMENDATIONS_REVIEW_NOTES_INPUT}
        disabled={isSaving}
        label="Review notes (optional)"
        maxLength={FEE_RECOMMENDATION_REVIEW_NOTES_MAX_LENGTH}
        name="reviewNotes"
        rows={4}
        value={reviewNotes}
        onChange={onChangeReviewNotes}
      />

      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} className="mt-16">
        <Button
          data-testid={LoanFeesTestIds.FEE_RECOMMENDATIONS_REJECT_CANCEL}
          disabled={isSaving}
          variant="line"
          onClick={onCancel}
        >
          Cancel
        </Button>

        <Button
          data-testid={LoanFeesTestIds.FEE_RECOMMENDATIONS_REJECT_SUBMIT}
          disabled={isSaving}
          form={REJECT_FEE_RECOMMENDATION_FORM_ID}
          type="submit"
        >
          <Box sx={buttonContentStyles}>
            {isSaving && <CircularProgress color="inherit" size={16} />}
            Reject
          </Box>
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

RejectFeeRecommendationModal.propTypes = {
  description: PropTypes.string.isRequired,
  onReject: PropTypes.func.isRequired,
  dialogId: PropTypes.string,
};

export default memo(RejectFeeRecommendationModal);
